import {
  Component,
  OnDestroy,
  input,
  CUSTOM_ELEMENTS_SCHEMA,
  OnInit,
  effect,
} from "@angular/core";

import { Editor, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Mention from "@tiptap/extension-mention";
import { Placeholder } from "@tiptap/extension-placeholder";
import { SuggestionProps } from "@tiptap/suggestion";

import {
  TiptapEditorDirective,
  // TiptapFloatingMenuDirective,
  // TiptapBubbleMenuDirective,
} from "ngx-tiptap";

import { SuggestionsService } from "./utils/suggestions.service";
import { sanitizeComment } from "../../helpers";

@Component({
  selector: "app-comments-text-editor",
  templateUrl: "./comments-text-editor.component.html",
  styleUrl: "./comments-text-editor.component.scss",
  imports: [
    TiptapEditorDirective,
    // TiptapFloatingMenuDirective,
    // TiptapBubbleMenuDirective,
    // TextEditorToolbarComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CommentsTextEditorComponent implements OnInit, OnDestroy {
  initialValue = input<string>("");
  placeholder = input<string>("Add a comment...");
  protected editor: Editor;
  protected value: string = "";
  #dirty = false;

  /** @ignore */
  constructor(public suggestions: SuggestionsService) {
    this.editor = new Editor({
      extensions: [
        Placeholder.configure({
          placeholder: this.placeholder(),
          emptyEditorClass: "is-editor-empty",
        }),
        StarterKit.configure({
          dropcursor: false,
          heading: false,
          horizontalRule: false,
          blockquote: false,
          codeBlock: false,
          bulletList: false,
          orderedList: false,
          paragraph: {
            HTMLAttributes: {
              class: "prose-p !my-1",
            },
          },
        }),
        Mention.configure({
          deleteTriggerWithBackspace: true,
          suggestion: {
            char: "@",
            items: ({ query }: any) => this.suggestions.search(query),
            render: () => {
              let suggestionsRef = this.suggestions;

              return {
                onStart: (props: SuggestionProps) => {
                  this.suggestions.onStart(props, props.decorationNode);
                },
                onUpdate(props: SuggestionProps) {
                  suggestionsRef.onUpdate(props);
                },
                onKeyDown(props: any) {
                  if (props.event.key === "Escape") {
                    suggestionsRef.clear();
                    return true;
                  }
                  return false;
                },
                onExit() {
                  suggestionsRef.clear();
                },
              };
            },
          },
          HTMLAttributes: {
            class: "inline-block my-1 px-1 bg-slate-200 rounded-full text-[0.9rem]",
          },
          renderText({ options, node }) {
            return `${options.suggestion.char}${
              node.attrs["name"] ?? node.attrs["email"]
            }`;
          },
          renderHTML({ options, node }) {
            return [
              "mention",
              mergeAttributes(
                { "data-email": node.attrs["email"] },
                { title: node.attrs["email"] },
                options.HTMLAttributes
              ),
              `${options.suggestion.char}${
                node.attrs["name"] ?? node.attrs["email"] ?? node.attrs["id"]
              }`,
            ];
          },
        }).extend({
          parseHTML() {
            return [{ tag: "mention" }];
          },
          addAttributes() {
            return {
              email: {
                parseHTML: (e) => e.getAttribute("data-email"),
              },
              name: {
                parseHTML: (e) => e.textContent,
              },
            };
          },
        }),
      ],
      editorProps: {
        attributes: {
          class:
            "prose prose-neutral overflow-auto min-h-[100px] max-h-[300px] max-w-none py-2 px-3 bg-white border-neutral-300 focus:border-neutral-600 border-1 rounded-md outline-none",
        },
      },
      onFocus: (_) => {
        if (this.#dirty) return;
        this.#dirty = true;
      },
    });

    effect(() => {
      try {
        this.editor.extensionManager.extensions.forEach((extension) => {
          if (extension.name === "placeholder") {
            (extension.options as any).placeholder = this.placeholder();
          }
        });
      } catch (ex) {
        // ignore
        console.error(ex);
      }
    });
  }

  get isDirty(): boolean {
    return this.#dirty;
  }

  /** Get plain text for checking for content */
  get text(): string {
    return this.editor.getText();
  }

  ngOnInit(): void {
    const content = this.initialValue() ?? "";
    this.editor.commands.setContent(content);
    if (content !== "") {
      this.#dirty = true;
    }
    this.suggestions.init();
  }

  ngOnDestroy(): void {
    this.editor.destroy();
  }

  /** Renders a parsed String with <mention> tags intact */
  public getOutput(): string {
    const result = sanitizeComment(this.editor.getHTML());
    return result;
  }

  public clear(): void {
    this.editor.commands.setContent("");
    this.#dirty = false;
  }
}
